import { ReactElement } from 'react'

import { QuickBuy as QuickBuyIcon, TypeStyle } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { PriceDisplay } from '../../../../../../brunswick/components/PriceDisplay'
import { VariantAttributeSelectors } from '../../../../../../brunswick/components/VariantAttributeSelectors/VariantAttributeSelectors'
import { CtaButton } from '../../../../../../components/atoms/CtaButton'
import { LinkWhen } from '../../../../../../components/pages/bulk/CategoryProductSection/LinkWhen'
import { trackEvent } from '../../../../../../lib/analytics'
import { shopTheLookTrackingCategory, viewProductAction } from '../constants'
import {
  ColorName,
  DesktopButtonCtaWrapper,
  DesktopContents,
  OpenQuickBuyModalButton,
  PriceAndColor,
  QuickBuyButtonWrapper,
  Tile,
  TileText,
  VariantAttributeSelectorsWrapper,
} from '../styles'

import { ShopTheLookTileImage } from './styles'
import { ViewModel } from './types'

export const Template = ({
  availableFitsKeys,
  category,
  colorGroups,
  ctaButton,
  currency,
  currentFitKey,
  currentSize,
  discountPrice,
  discountPriceExplicitFmt,
  fitNames,
  imageSource,
  isActiveProductWaitlistable,
  isCurrentColorCompletelySoldOut,
  onQuickBuyButtonClick,
  onProductView,
  pdpHref,
  price,
  priceExplicitFmt,
  priceRange,
  product,
  productGroupTitle,
  revealSizeChartModal,
  selectedColor,
  setSelectedColor,
  setSelectedFitKey,
  setSelectedSize,
  sizes,
  sizeSectionTitle,
  userHasAttemptedInvalidAddToBag,
}: ViewModel): ReactElement => {
  const {
    quickBuy: {
      overlay: { cta },
    },
  } = useTranslation()
  return (
    <Tile>
      {imageSource ? (
        <LinkWhen
          greaterThanOrEqual='lg'
          href={pdpHref}
          onClick={onProductView}
          {...trackEvent({ category: shopTheLookTrackingCategory, action: viewProductAction })}
        >
          <ShopTheLookTileImage
            className='flex'
            alt={product.handle ?? ''}
            aspectRatios={{
              sm: 1,
              md: 1,
              lg: 0.8,
            }}
            src={imageSource}
            loading={'lazy'}
            widths={{
              unit: 'px',
              sm: 85,
              md: 85,
              lg: 250, // We render at 200 width, but our height is 250 and these files are 1:1. So being safe here.
            }}
          />
        </LinkWhen>
      ) : null}

      <TileText>
        <LinkWhen
          greaterThanOrEqual='lg'
          href={pdpHref}
          onClick={onProductView}
          {...trackEvent({ category: shopTheLookTrackingCategory, action: viewProductAction })}
        >
          <TypeStyle.HeadlineExtraExtraSmall>{productGroupTitle}</TypeStyle.HeadlineExtraExtraSmall>
        </LinkWhen>

        <PriceAndColor>
          <PriceDisplay
            {...{
              currency,
              discountPrice,
              discountPriceExplicitFmt,
              price,
              priceExplicitFmt,
              priceRange,
            }}
          />

          {product.colorInfo?.name && <ColorName>{product.colorInfo.name}</ColorName>}
        </PriceAndColor>
      </TileText>

      <QuickBuyButtonWrapper>
        <OpenQuickBuyModalButton
          {...trackEvent({ category: shopTheLookTrackingCategory, action: 'quick buy' })}
          onClick={onQuickBuyButtonClick}
          aria-label={`${cta} ${productGroupTitle}`}
          size='large'
        >
          <QuickBuyIcon />
        </OpenQuickBuyModalButton>
      </QuickBuyButtonWrapper>

      <DesktopContents>
        <VariantAttributeSelectorsWrapper>
          <VariantAttributeSelectors
            {...{
              analyticsContext: { category: shopTheLookTrackingCategory },
              availableFitsKeys,
              category,
              colorGroups,
              currentFitKey,
              currentSize,
              fitNames,
              isActiveProductWaitlistable,
              isCurrentColorCompletelySoldOut,
              productGroupTitle,
              selectedColor,
              setSelectedColor,
              setSelectedFitKey,
              setSelectedSize,
              sizes,
              sizeSectionTitle,
              userHasAttemptedInvalidAddToBag,
              productHandle: product.handle,
            }}
            openSizeChartModal={revealSizeChartModal}
          />
        </VariantAttributeSelectorsWrapper>

        <DesktopButtonCtaWrapper>
          <CtaButton
            block
            onClick={ctaButton.onClick}
            {...trackEvent({
              category: shopTheLookTrackingCategory,
              action: 'add to bag',
            })}
            variant={ctaButton.variant}
          >
            {ctaButton.text}
          </CtaButton>
        </DesktopButtonCtaWrapper>
      </DesktopContents>
    </Tile>
  )
}
